import Konva from 'konva'

/**
 * Material Pattern Generator for Engineering Drawings
 * 
 * Creates professional material texture patterns following engineering standards:
 * - Wood: Grain pattern with lines
 * - Glulam: Laminated layers
 * - Concrete: Speckled/dotted pattern
 * - Steel: Cross-hatching or solid with metallic appearance
 */

export interface MaterialPatternOptions {
  width: number
  height: number
  scale?: number
}

/**
 * Create wood grain pattern (solid timber)
 * Standard representation: Parallel lines representing wood grain
 */
export const createWoodPattern = (options: MaterialPatternOptions): string => {
  const { width, height, scale = 1 } = options
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  canvas.width = width
  canvas.height = height

  // Base wood color
  ctx.fillStyle = '#D2B48C' // Tan color for wood
  ctx.fillRect(0, 0, width, height)

  // Draw grain lines
  ctx.strokeStyle = '#8B7355'
  ctx.lineWidth = 0.5 * scale

  const grainSpacing = 8 * scale
  for (let y = 0; y < height; y += grainSpacing) {
    ctx.beginPath()
    // Add some natural waviness to grain
    for (let x = 0; x < width; x += 2) {
      const waveOffset = Math.sin(x / 20) * 2
      ctx.lineTo(x, y + waveOffset)
    }
    ctx.stroke()
  }

  // Add occasional knots (circles)
  ctx.fillStyle = '#654321'
  for (let i = 0; i < 3; i++) {
    const x = Math.random() * width
    const y = Math.random() * height
    ctx.beginPath()
    ctx.arc(x, y, 2 * scale, 0, Math.PI * 2)
    ctx.fill()
  }

  return canvas.toDataURL()
}

/**
 * Create glulam (glued laminated timber) pattern
 * Standard representation: Horizontal lamination layers
 */
export const createGlulamPattern = (options: MaterialPatternOptions): string => {
  const { width, height, scale = 1 } = options
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  canvas.width = width
  canvas.height = height

  // Draw alternating lamination layers (fewer, thicker layers for realistic glulam)
  const layerHeight = 22 * scale
  const colors = ['#D2B48C', '#C19A6B', '#B8956A'] // Varying wood tones

  for (let y = 0; y < height; y += layerHeight) {
    const colorIndex = Math.floor(y / layerHeight) % colors.length
    ctx.fillStyle = colors[colorIndex]
    ctx.fillRect(0, y, width, layerHeight)

    // Glue line (dark thin line between layers)
    ctx.strokeStyle = '#654321'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()

    // Grain lines within each layer (fewer lines per layer)
    ctx.strokeStyle = '#8B7355'
    ctx.lineWidth = 0.5 * scale
    for (let yGrain = y + 5; yGrain < y + layerHeight; yGrain += 7 * scale) {
      ctx.beginPath()
      ctx.moveTo(0, yGrain)
      ctx.lineTo(width, yGrain)
      ctx.stroke()
    }
  }

  return canvas.toDataURL()
}

/**
 * Create concrete pattern
 * Standard representation: Speckled/aggregate pattern
 */
export const createConcretePattern = (options: MaterialPatternOptions): string => {
  const { width, height, scale = 1 } = options
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  canvas.width = width
  canvas.height = height

  // Base concrete color
  ctx.fillStyle = '#C0C0C0' // Light gray
  ctx.fillRect(0, 0, width, height)

  // Add aggregate (small stones/sand)
  const numAggregates = Math.floor((width * height) / (50 * scale * scale))
  
  for (let i = 0; i < numAggregates; i++) {
    const x = Math.random() * width
    const y = Math.random() * height
    const size = Math.random() * 2 * scale + 0.5
    
    // Varying gray tones for aggregate
    const grayValue = Math.floor(Math.random() * 80 + 100) // 100-180
    ctx.fillStyle = `rgb(${grayValue}, ${grayValue}, ${grayValue})`
    
    ctx.beginPath()
    ctx.arc(x, y, size, 0, Math.PI * 2)
    ctx.fill()
  }

  // Add some fine sand texture
  ctx.fillStyle = 'rgba(169, 169, 169, 0.3)'
  for (let i = 0; i < numAggregates * 3; i++) {
    const x = Math.random() * width
    const y = Math.random() * height
    ctx.fillRect(x, y, 0.5 * scale, 0.5 * scale)
  }

  return canvas.toDataURL()
}

/**
 * Create steel pattern
 * Standard representation: Cross-hatching or solid metallic
 */
export const createSteelPattern = (options: MaterialPatternOptions, variant: 'solid' | 'hatched' = 'hatched'): string => {
  const { width, height, scale = 1 } = options
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  canvas.width = width
  canvas.height = height

  if (variant === 'solid') {
    // Solid steel with metallic gradient
    const gradient = ctx.createLinearGradient(0, 0, width, 0)
    gradient.addColorStop(0, '#A8A8A8')
    gradient.addColorStop(0.5, '#D3D3D3')
    gradient.addColorStop(1, '#A8A8A8')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
  } else {
    // Hatched pattern (engineering standard for steel)
    ctx.fillStyle = '#B0B0B0'
    ctx.fillRect(0, 0, width, height)

    ctx.strokeStyle = '#505050'
    ctx.lineWidth = 1 * scale

    const spacing = 10 * scale

    // Diagonal lines (45 degrees)
    for (let i = -height; i < width + height; i += spacing) {
      ctx.beginPath()
      ctx.moveTo(i, 0)
      ctx.lineTo(i + height, height)
      ctx.stroke()
    }

    // Counter-diagonal lines (-45 degrees)
    for (let i = 0; i < width + height; i += spacing) {
      ctx.beginPath()
      ctx.moveTo(i, 0)
      ctx.lineTo(i - height, height)
      ctx.stroke()
    }
  }

  return canvas.toDataURL()
}

/**
 * Apply material pattern to a Konva shape
 */
export const applyMaterialPattern = (
  shape: Konva.Shape,
  material: 'wood' | 'glulam' | 'concrete' | 'steel',
  options: MaterialPatternOptions
): void => {
  let patternDataUrl: string

  switch (material) {
    case 'wood':
      patternDataUrl = createWoodPattern(options)
      break
    case 'glulam':
      patternDataUrl = createGlulamPattern(options)
      break
    case 'concrete':
      patternDataUrl = createConcretePattern(options)
      break
    case 'steel':
      patternDataUrl = createSteelPattern(options, 'hatched')
      break
    default:
      return
  }

  const img = new Image()
  img.onload = () => {
    shape.fillPatternImage(img)
    shape.getLayer()?.batchDraw()
  }
  img.src = patternDataUrl
}

/**
 * Get standard material colors (for simple fills without patterns)
 */
export const getMaterialColor = (material: 'wood' | 'glulam' | 'concrete' | 'steel'): string => {
  const colors = {
    wood: '#D2B48C',
    glulam: '#C19A6B',
    concrete: '#C0C0C0',
    steel: '#B0B0B0'
  }
  return colors[material]
}

